/**
 * SecureEmbed Energy Portal - ReportViewer Component
 *
 * Renders structured report data as styled content within the portal
 * Supports branded export with organisation logo, name, and footer
 */

import React from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { FileText, Calendar, Database, Printer } from 'lucide-react';
import { CHART_COLORS, RECHARTS_THEME } from '../constants/colors';
import DataSourceBadge from './DataSourceBadge';
import InfoTooltip from './InfoTooltip';
import { ReportBranding } from '../services/reportStorage';

interface ReportSection {
  type: 'summary' | 'chart' | 'table' | 'recommendations' | 'text';
  title?: string;
  content?: string;
  data?: any;
  chartType?: 'bar' | 'line';
}

interface ReportData {
  title: string;
  type: string;
  generated_at: string;
  datasources: string[];
  executive_summary?: string;
  sections: ReportSection[];
  stats?: Array<{ label: string; value: string | number }>;
}

interface ReportViewerProps {
  report: ReportData;
  onClose?: () => void;
  branding?: ReportBranding;
}

const ReportViewer: React.FC<ReportViewerProps> = ({ report, onClose, branding }) => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="report-viewer bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
      <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-[#2B064F] to-[#4A0E78] text-white print:bg-white print:text-black">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            {branding?.logoBase64 && (
              <img src={branding.logoBase64} alt="Organisation logo" className="h-12 w-auto rounded print:h-10" />
            )}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5" />
                <span className="text-sm opacity-80 uppercase tracking-wider">{report.type.replace(/_/g, ' ')}</span>
              </div>
              <h1 className="text-2xl font-bold">
                {branding?.orgName ? `${branding.orgName} \u2014 ` : ''}{report.title}
              </h1>
              <div className="flex items-center gap-4 mt-3 text-sm opacity-80">
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(report.generated_at).toLocaleString()}</span>
                <span className="flex items-center gap-1"><Database className="w-4 h-4" />{report.datasources.join(', ')}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 print:hidden">
            <button onClick={handlePrint} className="p-2 hover:bg-white/20 rounded-lg transition-colors" title="Print / Export PDF"><Printer className="w-5 h-5" /></button>
            {onClose && (<button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors text-xl leading-none">&times;</button>)}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8 print:p-4">
        {report.stats && report.stats.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {report.stats.map((stat, i) => {
              const statTooltipMap: Record<string, string> = { 'Total Insights': 'reports.stat.total-insights', 'Embeddings': 'reports.stat.embeddings', 'Pipeline Runs': 'report.stat.pipeline-runs' };
              const tipId = statTooltipMap[stat.label];
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-sm text-gray-500 mt-1 flex items-center justify-center gap-1">{stat.label}{tipId && <InfoTooltip tooltipId={tipId} />}</p>
                </div>
              );
            })}
          </div>
        )}

        {report.executive_summary && (
          <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Executive Summary</h2>
            <p className="text-gray-700 leading-relaxed">{report.executive_summary}</p>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500">Data sources:</span>
          {report.datasources.map(ds => (<DataSourceBadge key={ds} source={ds} size="md" />))}
        </div>

        {report.sections.map((section, idx) => (
          <div key={idx} className="space-y-3">
            {section.title && (
              <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-1.5">
                {section.title}
                {section.type === 'chart' && <InfoTooltip tooltipId="report.section.chart" />}
                {section.type === 'table' && <InfoTooltip tooltipId="report.section.table" />}
              </h2>
            )}
            {section.type === 'text' && section.content && (<p className="text-gray-700 leading-relaxed">{section.content}</p>)}
            {section.type === 'chart' && section.data && (
              <div className="print:break-inside-avoid">
                <ResponsiveContainer width="100%" height={280}>
                  {section.chartType === 'line' ? (
                    <LineChart data={section.data}><CartesianGrid {...RECHARTS_THEME.grid} /><XAxis dataKey="name" tick={RECHARTS_THEME.label} /><YAxis tick={RECHARTS_THEME.label} /><Tooltip contentStyle={RECHARTS_THEME.tooltip.contentStyle} /><Line type="monotone" dataKey="value" stroke={CHART_COLORS.primary} strokeWidth={2} dot={false} /></LineChart>
                  ) : (
                    <BarChart data={section.data}><CartesianGrid {...RECHARTS_THEME.grid} /><XAxis dataKey="name" tick={RECHARTS_THEME.label} /><YAxis tick={RECHARTS_THEME.label} /><Tooltip contentStyle={RECHARTS_THEME.tooltip.contentStyle} /><Bar dataKey="value" fill={CHART_COLORS.primary} radius={[6, 6, 0, 0]} /></BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            )}
            {section.type === 'table' && section.data && (
              <div className="overflow-x-auto print:break-inside-avoid">
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50"><tr>{Object.keys(section.data[0] || {}).map(key => (<th key={key} className="text-left py-2 px-3 font-medium text-gray-600 capitalize">{key.replace(/_/g, ' ')}</th>))}</tr></thead>
                  <tbody className="divide-y divide-gray-100">{section.data.map((row: any, ri: number) => (<tr key={ri} className="hover:bg-gray-50">{Object.values(row).map((val: any, ci: number) => (<td key={ci} className="py-2 px-3 text-gray-700">{typeof val === 'number' ? val.toLocaleString() : String(val)}</td>))}</tr>))}</tbody>
                </table>
              </div>
            )}
            {section.type === 'recommendations' && section.data && (
              <div className="space-y-2">
                {(section.data as string[]).map((rec, ri) => (
                  <div key={ri} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <span className="w-6 h-6 rounded-full bg-green-200 text-green-800 text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-semibold">{ri + 1}</span>
                    <p className="text-sm text-gray-700">{rec}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <div className="border-t border-gray-100 pt-4 text-xs text-gray-400 text-center">
          {branding?.footerText ? (<p>{branding.footerText}</p>) : (<p>Generated by SecureEmbed Energy Portal on {new Date(report.generated_at).toLocaleString()}</p>)}
        </div>
      </div>
    </div>
  );
};

export default ReportViewer;
